import { getOwner } from '@ember/owner';

import type { Handler, NextFn } from '@warp-drive/core/request';
import type {
  RequestContext,
  StructuredDataDocument,
} from '@warp-drive/core/types/request';

const MICROSOFT_AUTHENTICATOR = 'authenticator:microsoft';

type AuthenticatedSessionData = {
  authenticator?: string;
  token?: string;
};

type SessionService = {
  data?: {
    authenticated?: AuthenticatedSessionData;
  };
  invalidate?: () => Promise<void> | void;
  isAuthenticated?: boolean;
};

type MsalService = {
  getAccessToken(): Promise<{ accessToken?: string }>;
};

const AuthHandler: Handler = {
  async request<T>(
    context: RequestContext,
    next: NextFn<T>,
  ): Promise<StructuredDataDocument<T>> {
    const { request } = context;

    const headers = new Headers(request.headers);

    const owner = request.store ? getOwner(request.store) : undefined;
    const session = owner?.lookup('service:session') as
      SessionService | undefined;

    if (session?.isAuthenticated) {
      await addAuthorizationHeader(headers, session, owner);
    }

    return next({
      ...request,
      headers,
    });
  },
};

export default AuthHandler;

async function addAuthorizationHeader(
  headers: Headers,
  session: SessionService,
  owner: ReturnType<typeof getOwner>,
): Promise<void> {
  const { authenticator, token } = session.data?.authenticated ?? {};

  if (authenticator === MICROSOFT_AUTHENTICATOR) {
    const msal = owner?.lookup('service:msal') as MsalService | undefined;

    try {
      const tokenResponse = await msal?.getAccessToken();

      if (tokenResponse?.accessToken) {
        headers.set('Authorization', `Bearer ${tokenResponse.accessToken}`);
      }
    } catch {
      await session.invalidate?.();
    }

    return;
  }

  if (token) {
    headers.set('X-UMG-BACKEND-TOKEN', token);
  }
}
